import { Button } from "@mui/material";
import { useFormik } from "formik";
import { ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Flex from "@/components/Flex";
import LoadingOverlay from "@/components/LoadingOverlay";
import { ScrollArea } from "@/components/ScrollBar";
import { appPaths } from "@/constants/paths";
import { useSnackbar } from "@/hooks/useSnackbar";
import { useNewTemplateCreate } from "@/queries/template/new-flow/use-new-template-create";
import { useNewTemplateUpdate } from "@/queries/template/new-flow/use-new-template-update";
import { RosTemplate } from "@/services";
import { getViewAs } from "@/utils/auth-storage";
import {
  showToastOnApiCall,
  getUnNecessaryTransformData,
  getXtenantId,
} from "@/utils/utils";
import EmailForm from "./EmailForm";
import SmsForm from "./SmsForm";
import TemplateBasicReqFields from "./components/TemplateBasicReqFields";
import TemplateTitle from "./components/TemplateTitle";
import {
  TEMPLATE_FORM_INITIAL_VALUES,
  TEMPLATE_VALIDATION_SCHEMA,
  newPeTemplateInitialData,
  newRosTemplateInitialData,
} from "./template-constant";
import Micro from "./template-forms/Micro";
import PeRosForm from "./template-forms/pe-template/PeRosForm";
import CarePlanForm from "./template-forms/care-plan/CarePlanForm";
import HomeCareRosForm from "./home-care-ros/HomeCareRosForm";
import { HOME_CARE_ROS_INITIAL_DATA } from "./home-care-ros/home-care-ros-data";
import { TemplateTypeValue } from "./template-types";

const TEMPLATE_INITIAL_DATA = {
  PHYSICAL: newPeTemplateInitialData,
  ROS: newRosTemplateInitialData,
  HOME_CARE_ROS: HOME_CARE_ROS_INITIAL_DATA,
  HOME_CARE_PE: HOME_CARE_ROS_INITIAL_DATA,
} as const;

interface FormBuilderHostProps {
  isLoading?: boolean;
  preFillSchema?: any;
  enableOnlyAddApi?: boolean;
  onClose?: () => void;
}

/**
 * @author [Rajkumar Rathod]
 */
const TemplateNewForm = ({
  isLoading,
  preFillSchema,
  enableOnlyAddApi,
  onClose,
}: FormBuilderHostProps) => {
  const formik = useFormik<RosTemplate>({
    initialValues: preFillSchema || TEMPLATE_FORM_INITIAL_VALUES,
    validationSchema: TEMPLATE_VALIDATION_SCHEMA,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  const isProvider = getViewAs() === "provider";

  const showSnackbar = useSnackbar();
  const navigate = useNavigate();

  const templateType = formik.values?.templateTypes as
    TemplateTypeValue | undefined;

  useEffect(() => {
    if (
      !preFillSchema &&
      (templateType === "PHYSICAL" ||
        templateType === "ROS" ||
        templateType === "HOME_CARE_ROS" ||
        templateType === "HOME_CARE_PE")
    ) {
      formik.setValues((prev) => ({
        ...prev,
        rosTemplateTypeSet: TEMPLATE_INITIAL_DATA[templateType],
      }));
    }
  }, [templateType]);

  const { mutateAsync: createTemplate } = useNewTemplateCreate();
  const { mutateAsync: updateTemplate } = useNewTemplateUpdate();

  function handleSubmit(values: RosTemplate) {
    const transformedPayload = {
      rosTemplate: getUnNecessaryTransformData(
        ["speciality", "icdCodes"],
        values
      ),
      xTENANTID: getXtenantId(),
    };
    if (enableOnlyAddApi) {
      showToastAndResetForm(createTemplate(transformedPayload));
      return;
    }

    if (!preFillSchema)
      showToastAndResetForm(createTemplate(transformedPayload));
    else showToastAndResetForm(updateTemplate(transformedPayload));
  }

  function handleNavigateBack() {
    if (!enableOnlyAddApi) {
      navigate(
        isProvider
          ? "/app/provider/library/templates"
          : appPaths.libraryTemplatesPath
      );
    }
    onClose?.();
  }

  async function showToastAndResetForm(apiCall: Promise<any>) {
    showToastOnApiCall({
      showSnackbar,
      apiCall,
      onSave: () => formik.resetForm(),
      onDelay: handleNavigateBack,
    });
  }

  const ActiveFormMapper: Record<TemplateTypeValue, ReactNode> = {
    EMAIL: <EmailForm formik={formik} />,
    SMS: <SmsForm formik={formik} />,
    PHYSICAL: <PeRosForm formik={formik} />,
    MACROS: <Micro formik={formik} />,
    ROS: <PeRosForm formik={formik} />,
    HOME_CARE_ROS: <HomeCareRosForm formik={formik} />,
    HOME_CARE_PE: <HomeCareRosForm formik={formik} />,
    CARE_PLAN: <CarePlanForm formik={formik} />,
  };

  return (
    <Flex
      flexDirection={"column"}
      width={"100%"}
      height={"calc(100vh - 100px)"}
      justifyContent={"space-between"}
    >
      <Flex flexDirection={"column"} gap={1}>
        <LoadingOverlay isLoading={!!isLoading} />
        {!enableOnlyAddApi && (
          <TemplateTitle handleNavigateBack={handleNavigateBack} />
        )}

        <TemplateBasicReqFields prefillData={preFillSchema} formik={formik} />

        <ScrollArea
          width={"100%"}
          height={`calc(100vh - ${formik.isValid ? "250px" : "320px"})`}
        >
          {/* all active form on basis of type */}
          {templateType ? ActiveFormMapper[templateType] : <></>}
        </ScrollArea>
      </Flex>

      <Flex justifyContent={"end"} mt={1}>
        <Button variant="contained" onClick={formik.submitForm}>
          Save
        </Button>
      </Flex>
    </Flex>
  );
};

export default TemplateNewForm;
